import * as React from "react";

import { useMetrics } from "../../../../Internals/Metrics/MetricsProviderContext";
import { UNIT_LIST_COLLAPSE } from "../../../../metricsAction";
import { NestedLevelContext } from "../../NestedLevelContext";
import { UnitId } from "../../../Unit/UnitId/UnitId";

import {
    Action,
    ActionWrapper,
    CollapseIcon,
    CollapseIconButton,
    Content,
    ExtraInfo,
    MenuItemRoot,
    MultipleIndex,
    Name,
    Row,
    UnitItemTitle,
} from "./MenuItem.styles";

export interface MenuItemProps {
    id?: UnitId;
    onClick?: (e: React.MouseEvent) => void;
    name?: string;
    action?: React.ReactNode;
    showActionOnHover?: boolean;
    active?: boolean;
    childActive?: boolean;
    empty?: boolean;
    children?: React.ReactNode;
    content?: React.ReactNode;
    collapsible?: boolean;
    collapsed?: boolean;
    onCollapseChange?: (collapsed: boolean) => void;
    index?: number;
    count?: number;
    setCollapsedUnitItem?: (
        isCollapsed: boolean,
        fufDraftId?: string,
        instance?: string,
        instances?: string[],
        name?: string
    ) => void;
    currentInstance?: string;
    parentInstances?: string[];
    fufDraftId?: string;
    extraInfo?: string;
    label?: React.ReactNode;
}

export function MenuItem(props: MenuItemProps): JSX.Element {
    const track = useMetrics();
    const nestedLevel = React.useContext(NestedLevelContext);

    const handleCollapseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        track(UNIT_LIST_COLLAPSE);
        props.onCollapseChange?.(!props.collapsed);
        props.setCollapsedUnitItem &&
            props.collapsible &&
            props.setCollapsedUnitItem(
                !props.collapsed,
                props.fufDraftId,
                props.currentInstance,
                props.parentInstances,
                props.name
            );
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onClick?.(e);
    };

    const paddingLeft = nestedLevel * 16;

    return (
        <MenuItemRoot>
            <Content
                active={props.active}
                childActive={props.childActive}
                empty={props.empty}
                collapsible={props.collapsible ?? false}>
                <Row style={{ paddingLeft: paddingLeft }}>
                    {props.collapsible && (
                        <CollapseIconButton
                            collapsed={props.collapsed ?? false}
                            onClick={handleCollapseClick}
                            data-tid="collapseButton">
                            <CollapseIcon name="ArrowCDownIcon" size={12} />
                        </CollapseIconButton>
                    )}
                    {props.index !== undefined && <MultipleIndex>{props.index + 1}</MultipleIndex>}
                    <UnitItemTitle onClick={handleClick} childActive={props.childActive ?? false}>
                        <Name>{props.name}</Name>
                        {props.label}
                    </UnitItemTitle>
                    {props.count !== undefined && props.count > 0 && (
                        <ExtraInfo onClick={handleClick}>{props.count}</ExtraInfo>
                    )}
                    <ActionWrapper>
                        {props.action != undefined && (
                            <Action visible={!props.showActionOnHover}>{props.action}</Action>
                        )}
                    </ActionWrapper>
                </Row>
                {props.extraInfo && (
                    <ExtraInfo onClick={handleClick} paddingLeft={paddingLeft} data-tid="extraInfo">
                        {props.extraInfo}
                    </ExtraInfo>
                )}
                {props.content}
            </Content>
            {props.children}
        </MenuItemRoot>
    );
}
